// @givmo/fundraisers-module — the email and its preview as the designer reads them (EMC-WEB10, over the
// backend's EMC-BE2/BE5 `/org/{org_ref}/fundraisers/{fundraiser_id}/emails/*`).
//
// ⚠ EVERY FACT IS THE SERVER'S: the email's words, its render (the footer, the links and images, the
// contrast floors are the renderer's), whether it can change. This file reads what the server answered
// and fails closed where a read would otherwise claim something it did not say.

import { extractList } from '@givmo/api-client';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
const text = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/** The states a person's change is taken in: a draft, a cancelled email, a scheduled one not frozen. */
const CHANGEABLE_STATES = new Set(['draft', 'cancelled', 'scheduled']);

export interface DesignerEmail {
  id: string;
  state: string;
  subject: string;
  preheader: string;
  /** The message as stored, for the undo's restore; null where this version can't read it. */
  body: Record<string, unknown> | null;
  /** How many top-level blocks the message holds (0 for an empty email). */
  blockCount: number;
  frozen: boolean;
  scheduledFor: string | null;
  tz: string | null;
  /** When it last changed: a hand edit names it, and is refused if the email has moved on since. */
  updatedAt: string | null;
}

export function decodeDesignerEmail(value: unknown): DesignerEmail | null {
  if (!isRecord(value)) return null;
  const id = text(value.id);
  const state = text(value.state);
  if (id === null || state === null) return null;
  const body = value.body_readable !== false && isRecord(value.body_json) ? value.body_json : null;
  const blocks = body !== null && Array.isArray(body.blocks) ? body.blocks.length : 0;
  return {
    id,
    state,
    subject: text(value.subject) ?? '',
    preheader: text(value.preheader) ?? '',
    body,
    blockCount: blocks,
    frozen: value.frozen === true,
    scheduledFor: text(value.scheduled_for),
    tz: text(value.tz),
    updatedAt: text(value.updated_at),
  };
}

/** Whether the assistant (and the undo) may change it now: the server refuses the rest anyway. */
export function designerEmailChangeable(email: DesignerEmail): boolean {
  return CHANGEABLE_STATES.has(email.state) && !(email.state === 'scheduled' && email.frozen);
}

/** Whether the email has anything in it yet: a subject, preview text or a block. */
export function designerEmailEmpty(email: DesignerEmail): boolean {
  return email.subject.trim() === '' && email.preheader.trim() === '' && email.blockCount === 0;
}

/** The email's place in its series ("Email 4 of 9"), or null where it isn't listed. */
export function seriesPosition(
  series: unknown,
  emailId: string,
): { index: number; total: number } | null {
  const ids = extractList<unknown>(series)
    .map((row) => (isRecord(row) ? text(row.id) : null))
    .filter((id): id is string => id !== null);
  const at = ids.indexOf(emailId);
  return at < 0 ? null : { index: at + 1, total: ids.length };
}

export interface DesignerPreview {
  /** The server's render; null where this version can't read the body. */
  html: string | null;
  subject: string;
  preheader: string;
  fromName: string | null;
  fromAddress: string | null;
}

export function decodeDesignerPreview(body: unknown): DesignerPreview {
  const value = isRecord(body) ? body : {};
  const rendered = isRecord(value.rendered) ? value.rendered : {};
  return {
    html: rendered.readable === true ? text(rendered.html) : null,
    subject: text(rendered.subject) ?? '',
    preheader: text(rendered.preheader) ?? '',
    fromName: text(rendered.from_name),
    fromAddress: text(rendered.from_address),
  };
}

/** The test's answer: the caller's own address it went to. */
export function decodeDesignerTest(body: unknown): string | null {
  return isRecord(body) ? text(body.to) : null;
}

/**
 * A reply split at its "Changes:" line (the designer's prompt ends a reply that changed the email with
 * one). Text before it is the reply; the line after the label is the facts. A reply with no such line is
 * all reply.
 */
export function splitChangesLine(reply: string): { reply: string; changes: string | null } {
  const match = /(^|\n)\s*Changes:\s*([\s\S]*)$/.exec(reply);
  if (match === null) return { reply: reply.trim(), changes: null };
  const changes = match[2]!.replace(/\s+/g, ' ').trim();
  return {
    reply: reply.slice(0, match.index).trim(),
    changes: changes.length > 0 ? changes : null,
  };
}

/**
 * A designer reply's closing "Used … branding" line, which the SERVER writes when the turn read the
 * organization's brand (`email_designer_sources.py`): split off so the page draws it above the reply (14b).
 * A reply without one is unchanged.
 */
export function splitSourcesLine(reply: string): { reply: string; sources: string | null } {
  const match = /(?:^|\n)\s*(Used [^\n]+? branding(?: and Collateral)?)\s*$/.exec(reply);
  if (match === null) return { reply, sources: null };
  return { reply: reply.slice(0, match.index).trimEnd(), sources: match[1]! };
}
